// The portrait's WebGL layers: the X-ray, the face dots, the lens dots and their sheen, and
// the dot butterfly, each dressed in the theme's look (portrait-look.ts).
//
// Three canvases, each with its own context:
// - The face (about 11900 dots) only redraws when a wave or the theme changes it
//   (`DotFieldLayer` skips idle frames). Its own canvas holds that frame while the lens
//   drifts. Sharing a canvas with the lens would mean drawing the face 60 times a second.
//   The X-ray draws first in the face's context: it only shows during a wave, when the face
//   draws every frame anyway.
// - The lens (877 dots) drifts every frame, and its sheen draws on top in a second pass of
//   the same program.
// - The butterfly needs a depth buffer, and it shrinks to 1 x 1 while WebGPU draws it.

import {
  DotFieldLayer,
  FACE_OUTLINE_FADE,
  LensFieldLayer,
  WAVE_ORIGIN,
  WingPlaneLayer,
  buildLensPalette,
  computeFaceLayout,
  dotClipTransform,
  lensCoreMask,
  lensDotParams,
  outlineFade,
  selectDots,
  wingPlaneScatter,
  type DotGroups,
  type FaceLayout,
  type HalftoneData,
  type LensFieldData,
  type RenderState,
  type WingStats,
  XrayLayer,
  type XrayDots,
} from "@bfly/engine";
import { mulberry32 } from "@bfly/engine/random";
import { DARK_LOOK, gradePalette, LIGHT_LOOK, type PortraitLook } from "./portrait-look.js";
import { currentTheme } from "../page/theme.js";
import type { WingPlanePose } from "../monarch/wing-pose.js";

// How strongly the lens sheen draws its oil film.
const LENS_SHEEN_STRENGTH = 0.22;

// The X-ray's dots on the dark page: a cold blue white. On the light page they take the ink,
// so the bones read dark, like a print of a radiograph.
const XRAY_COLD_WHITE = [0.84, 0.91, 1] as const;

// A 2D context turns any CSS color into RGB, `oklch()` included. Its pixel gets read back, so
// it lives in CPU memory and a read doesn't stall the GPU.
const groundContext = document
  .createElement("canvas")
  .getContext("2d", { willReadFrequently: true });

/** A CSS color as 0 to 1 sRGB. */
function srgb(color: string): [number, number, number] {
  const ctx = groundContext;
  if (!ctx) return [1, 1, 1];
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r = 255, g = 255, b = 255] = ctx.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
}

/** The page's background color, as 0 to 1 sRGB. */
function pageGround(): [number, number, number] {
  return srgb(getComputedStyle(document.body).backgroundColor);
}

function must<T extends Element>(root: ParentNode, selector: string, type: new () => T): T {
  const element = root.querySelector(selector);
  if (!(element instanceof type)) throw new Error(`Portrait: missing ${selector}`);
  return element;
}

function acquireContext(canvas: HTMLCanvasElement, depth: boolean): WebGL2RenderingContext {
  const gl = canvas.getContext("webgl2", { alpha: true, premultipliedAlpha: true, depth });
  if (!gl) throw new Error("Portrait: WebGL2 not available");
  return gl;
}

/** The lens layers' data: the lens dots, their palette ranks, and their drift. */
function lensFieldData(data: HalftoneData, lensIndices: Uint32Array): LensFieldData {
  const lensX = Float32Array.from(lensIndices, (j) => data.x[j] ?? 0);
  const lensY = Float32Array.from(lensIndices, (j) => data.y[j] ?? 0);
  const { lensPal, lensPalN, baseRank } = buildLensPalette(
    data.colors,
    data.colorIndex,
    lensIndices,
  );
  // A fixed seed, so the lens drifts the same way on every visit.
  const { ph, ph2, ampJ } = lensDotParams(mulberry32(0x1efb), lensX, lensY);
  return {
    count: lensIndices.length,
    x: lensX,
    y: lensY,
    r: Float32Array.from(lensIndices, (j) => data.r[j] ?? 0),
    baseRank,
    ph,
    ph2,
    ampJ,
    core: lensCoreMask(lensX, lensY),
    lensPal,
    lensPalN,
  };
}

export class PortraitLayers {
  layout: FaceLayout = computeFaceLayout(1, 1);
  look: Readonly<PortraitLook> = LIGHT_LOOK;
  // While WebGPU draws the butterfly on its own, the hidden WebGL butterfly shrinks to a
  // 1 x 1 buffer.
  private butterflyBuffer = true;
  // The X-ray, once its dots arrive: it waits until no flash shows, so it never pops in
  // halfway through one.
  private xrayWaiting: XrayLayer | null = null;
  private xray: XrayLayer | null = null;
  private disposed = false;

  private constructor(
    private readonly portrait: HTMLElement,
    private readonly canvases: Readonly<Record<"face" | "lens" | "butterfly", HTMLCanvasElement>>,
    private readonly colors: readonly string[],
    private readonly face: DotFieldLayer,
    private readonly lens: LensFieldLayer,
    private readonly butterfly: WingPlaneLayer,
    /** The butterfly's dots. The WebGPU butterfly draws them too. */
    readonly butterflyData: HalftoneData,
    /** How far the face reaches from where waves start (the butterfly) to the far edge of
        its farthest dot, in artwork units. The X-ray shows until a wave's ring passes it. */
    readonly faceReach: number,
  ) {
    // The butterfly keeps the baked palette in both themes, like the WebGPU monarch, so the
    // two renderers match at the handoff.
    butterfly.setPalette(colors);
    this.applyLook();
  }

  static async create(
    portrait: HTMLElement,
    data: HalftoneData,
    groups: DotGroups,
  ): Promise<PortraitLayers> {
    const canvases = {
      face: must(portrait, ".face", HTMLCanvasElement),
      lens: must(portrait, ".lens", HTMLCanvasElement),
      butterfly: must(portrait, ".bfly-cv", HTMLCanvasElement),
    };
    // Separate layers, so the lens dots stay rigid and the wings can move on their own.
    const faceExclude = new Set([...(groups.butterfly ?? []), ...(groups.lens ?? [])]);
    const faceDots = new Set<number>();
    for (let i = 0; i < data.n; i++) if (!faceExclude.has(i)) faceDots.add(i);
    // The outer dots of the face and the butterfly fade out, so their outlines don't end in
    // a hard edge against the page.
    const faceData = {
      ...selectDots(data, faceExclude, "exclude"),
      opacity: outlineFade(data, faceDots, FACE_OUTLINE_FADE),
    };
    const butterflyDots = new Set(groups.butterfly ?? []);
    const butterflyData = {
      ...selectDots(data, butterflyDots, "keep"),
      opacity: outlineFade(data, butterflyDots),
    };
    const lensData = lensFieldData(data, groups.lens ?? new Uint32Array(0));
    let faceReach = 0;
    for (const i of faceDots) {
      const reach = Math.hypot(
        (data.x[i] ?? 0) - WAVE_ORIGIN.x * data.w,
        (data.y[i] ?? 0) - WAVE_ORIGIN.y * data.w,
      );
      faceReach = Math.max(faceReach, reach + (data.r[i] ?? 0));
    }
    // The layers set up in parallel, each on its own context. Only the butterfly planes use
    // a depth test. If one fails, the others release what they made before the error is
    // rethrown. The async wrappers turn a synchronous throw (no context) into a rejection,
    // so it settles like the others.
    const settled = await Promise.allSettled([
      (async () => DotFieldLayer.create(acquireContext(canvases.face, false), faceData))(),
      (async () => LensFieldLayer.create(acquireContext(canvases.lens, false), lensData))(),
      (async () =>
        WingPlaneLayer.create(acquireContext(canvases.butterfly, true), butterflyData))(),
    ]);
    const [face, lens, butterfly] = settled;
    if (
      face.status === "rejected" ||
      lens.status === "rejected" ||
      butterfly.status === "rejected"
    ) {
      for (const result of settled) if (result.status === "fulfilled") result.value.dispose();
      throw settled.find((result): result is PromiseRejectedResult => result.status === "rejected")
        ?.reason;
    }
    // The constructor applies the look to the layers. If that throws, release them too.
    try {
      return new PortraitLayers(
        portrait,
        canvases,
        data.colors,
        face.value,
        lens.value,
        butterfly.value,
        butterflyData,
        faceReach,
      );
    } catch (error) {
      for (const layer of [face.value, lens.value, butterfly.value]) layer.dispose();
      throw error;
    }
  }

  /** The dot butterfly's canvas. The WebGPU butterfly takes its place. */
  get butterflyCanvas(): HTMLCanvasElement {
    return this.canvases.butterfly;
  }

  /** Applies the theme's look: the face and lens inks, how they blend with the page, the
      face's dots, and the butterfly's far wing. */
  applyLook(): void {
    this.look = currentTheme() === "dark" ? DARK_LOOK : LIGHT_LOOK;
    const palette = gradePalette(this.colors, this.look.chroma);
    this.face.setPalette(palette);
    this.lens.setPalette(palette);
    const ground = pageGround();
    this.face.setBlend(this.look.blend, ground);
    this.lens.setBlend(this.look.blend, ground);
    this.face.setLook(this.look.dots);
    for (const xray of [this.xray, this.xrayWaiting]) xray?.setColor(this.xrayColor());
  }

  /** Builds the X-ray in the face's context. It starts to draw at the first frame with no
      flash (`setXrayStrength(0)`). */
  async attachXray(dots: XrayDots): Promise<void> {
    const gl = this.canvases.face.getContext("webgl2");
    if (!gl || this.disposed || this.xray || this.xrayWaiting) return;
    const xray = await XrayLayer.create(gl, dots);
    if (this.disposed) {
      xray.dispose();
      return;
    }
    xray.setTransform(dotClipTransform(this.layout));
    xray.resize(this.layout.deviceW);
    xray.setColor(this.xrayColor());
    this.xrayWaiting = xray;
  }

  /** How strongly the X-ray shows, 0 to 1. The face redraws when it changes. */
  setXrayStrength(strength: number): void {
    if (this.xrayWaiting && strength === 0) {
      this.xray = this.xrayWaiting;
      this.xrayWaiting = null;
      this.face.setUnderlay(this.xray);
    }
    if (!this.xray || this.xray.strength === strength) return;
    this.xray.setStrength(strength);
    this.face.invalidate();
  }

  private xrayColor(): readonly [number, number, number] {
    return this.look === DARK_LOOK ? XRAY_COLD_WHITE : srgb(getComputedStyle(document.body).color);
  }

  /** Sizes the buffers to the portrait. */
  size(): void {
    // The layout width, not the client rect, because the portrait is rotated.
    this.layout = computeFaceLayout(
      this.portrait.offsetWidth,
      Math.min(window.devicePixelRatio || 1, 2),
    );
    const clip = dotClipTransform(this.layout);
    for (const [canvas, layer] of [
      [this.canvases.face, this.face],
      [this.canvases.lens, this.lens],
    ] as const) {
      canvas.width = this.layout.deviceW;
      canvas.height = this.layout.deviceH;
      layer.resize(this.layout.deviceW, this.layout.deviceH);
      layer.setTransform(clip);
    }
    for (const xray of [this.xray, this.xrayWaiting]) {
      xray?.resize(this.layout.deviceW);
      xray?.setTransform(clip);
    }
    this.sizeButterfly();
  }

  /** Whether the dot butterfly keeps a full buffer. It does, unless WebGPU is drawing on its
      own. */
  keepButterflyBuffer(keep: boolean): void {
    this.butterflyBuffer = keep;
    this.sizeButterfly();
  }

  render(state: RenderState): void {
    this.face.render(state);
    this.lens.render(state, LENS_SHEEN_STRENGTH);
  }

  renderButterfly(now: number, pose: WingPlanePose, kind: WingStats["kind"]): void {
    const scatter = wingPlaneScatter(kind, pose.nearTurnRad, pose.farTurnRad);
    this.butterfly.render({
      home: dotClipTransform(this.layout),
      now,
      nearRotation: pose.near,
      farRotation: pose.far,
      nearScatter: scatter.near,
      farScatter: scatter.far,
      farOpacity: this.look.farWing.opacity,
      farShade: this.look.farWing.shade,
    });
  }

  /** Releases the layers and loses their contexts, so the GPU memory frees up right away
      (when the portrait disconnects). The canvases can't draw again after this. */
  dispose(): void {
    this.disposed = true;
    for (const layer of [this.face, this.lens, this.butterfly]) layer.dispose();
    for (const xray of [this.xray, this.xrayWaiting]) xray?.dispose();
    for (const canvas of Object.values(this.canvases))
      canvas.getContext("webgl2")?.getExtension("WEBGL_lose_context")?.loseContext();
  }

  /** Calls `listener` when a context is lost (each canvas reports once). After that, no layer
      draws again on this visit. */
  onContextLost(listener: () => void, signal: AbortSignal): void {
    for (const canvas of Object.values(this.canvases))
      canvas.addEventListener("webglcontextlost", listener, { once: true, signal });
  }

  private sizeButterfly(): void {
    const canvas = this.canvases.butterfly;
    canvas.width = this.butterflyBuffer ? this.layout.deviceW : 1;
    canvas.height = this.butterflyBuffer ? this.layout.deviceH : 1;
    this.butterfly.resize(canvas.width, canvas.height);
  }
}
